import { useCallback, useContext, useEffect, useRef } from "react";
import { AppContext } from "../contexts/AppContext";

type ScheduleEntry = {
  views: { reference_key: string; to_document_id: number }[];
  scheduleType: "time" | "trigger";
  days?: string[];
  trigger?: { protocol: "tcp" | "udp" | "websocket" | "remote"; command: string };
  timeRange?: { start: string; end: string };
  name: string;
};

const getDayName = (date: Date) =>
  ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][
    date.getDay()
  ];

export function useContentSchedule(schedule: ScheduleEntry[] | undefined) {
  const { scheduledIds, setScheduledIds } = useContext(AppContext) || {};
  const isOverriddenByScheduleRef = useRef(false);

  const checkSchedule = useCallback(() => {
    if (!schedule || schedule.length === 0) return undefined;

    const now = new Date();

    /**
     * Returns the current date/time parts in the Europe/Oslo timezone.
     *
     * Using Intl.DateTimeFormat with an explicit IANA timezone ensures
     * daylight saving time (DST) is handled automatically.
     */
    const getOsloDateTimeParts = (date: Date) => {
      const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Oslo", weekday: "long", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23"}).formatToParts(date);
      const getPart = (type: Intl.DateTimeFormatPartTypes) => parts.find(part => part.type === type)?.value;

      return { day: getPart("weekday")?.toLowerCase(), hours: Number(getPart("hour")), minutes: Number(getPart("minute")), seconds: Number(getPart("second"))};
    };

    /**
     * Converts a time value to the number of seconds since midnight.
     *
     * Supported formats:
     * - "16:00"
     * - "16:00:30"
     * - "2026-08-19T16:00:00.230Z"
     *
     * If an ISO datetime is provided, the date portion is ignored and
     * only the clock time is used.
     */
  const getTimeInSeconds = (value?: string): number | null => {
    if (!value) return null;

    // ISO datetime
    if (value.includes("T")) {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) { return null; }

      return (date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds());
    }

    // Plain local time
    const [hours, minutes, seconds = "0"] = value.split(":");

    const h = Number(hours);
    const m = Number(minutes);
    const s = Number.parseFloat(seconds);

    if (Number.isNaN(h) || Number.isNaN(m) || Number.isNaN(s) || h < 0 || h > 23 || m < 0 || m > 59 || s < 0 || s >= 60) {
      return null;
    }

    return h * 3600 + m * 60 + s;
  };

    const { day: currentDay, hours, minutes, seconds } = getOsloDateTimeParts(now);
    const nowInSeconds = hours * 3600 + minutes * 60 + seconds;

    for (const entry of schedule) {
      if (entry.scheduleType !== "time") {
        continue;
      }

      if (!currentDay || !entry.days?.some(day => day.toLowerCase() === currentDay)) {
        continue;
      }

      const startTime = getTimeInSeconds(entry.timeRange?.start);
      const endTime = getTimeInSeconds(entry.timeRange?.end);

      if (startTime === null || endTime === null) {
        continue;
      }

      /**
       * Normal range:
       * 08:00 -> 16:00
       *
       * Overnight range:
       * 16:00 -> 02:00
       */
      const isWithinTimeRange = startTime <= endTime ? nowInSeconds >= startTime && nowInSeconds <= endTime : nowInSeconds >= startTime || nowInSeconds <= endTime;

      if (isWithinTimeRange) {
        return (
          entry.views?.map(view => view.to_document_id) || undefined
        );
      }
    }

    return undefined;
  }, [schedule]);

  useEffect(() => {
    const updateContent = () => {
      if (!isOverriddenByScheduleRef.current) {
        const newContent = checkSchedule();
        setScheduledIds && setScheduledIds(newContent);
      }
    };

    updateContent();

    const millisUntilNextMinute = (60 - new Date().getSeconds()) * 1000;
    let interval: ReturnType<typeof setInterval> | undefined;
    const timeout = setTimeout(() => {
      updateContent();
      interval = setInterval(updateContent, 60000);
    }, millisUntilNextMinute);

    return () => {
      clearTimeout(timeout);
      if (interval) { clearInterval(interval); }
    };
 
  }, [schedule, setScheduledIds, checkSchedule]);

  // ✅ **Håndter flere protokoller for å trigge innhold**
  useEffect(() => {
    const handleCommand = (protocol: string, command: string) => {
      console.log(`Network task received on '${protocol}': ${command}`);
      const match = schedule?.find(
        (entry) => entry.scheduleType === "trigger" && entry.trigger?.protocol === protocol && entry.trigger?.command === command
      );

      if (match) {
        isOverriddenByScheduleRef.current = true;
        setScheduledIds(match?.views?.map(view => view.to_document_id) || undefined);
      }
    };

    const remoteListener = (e: Event) => handleCommand("remote", (e as CustomEvent).detail?.command);
    const websocketListener = (e: Event) => handleCommand("websocket", (e as CustomEvent).detail?.msg);
    const tcpListener = (e: Event) => handleCommand("tcp", (e as CustomEvent).detail?.data);
    const udpListener = (e: Event) => handleCommand("udp", (e as CustomEvent).detail?.data);
    function keyListener(event) {
      if (event.key === "a") { handleCommand("tcp", "play1") }
      if (event.key === "b") { handleCommand("tcp", "play2") }
      if (event.key === "c") { handleCommand("tcp", "play3") }
      if (event.key === "d") { handleCommand("tcp", "stop") }
    }

    document.addEventListener("remoteCommand", remoteListener);
    document.addEventListener("socketMessage", websocketListener);
    document.addEventListener("tcpMessage", tcpListener);
    document.addEventListener("udpMessage", udpListener);
    document.addEventListener("keydown", keyListener);

    return () => {
      document.removeEventListener("remoteCommand", remoteListener);
      document.removeEventListener("socketMessage", websocketListener);
      document.removeEventListener("tcpMessage", tcpListener);
      document.removeEventListener("udpMessage", udpListener);
      document.removeEventListener("keydown", keyListener);
    };
  }, [schedule, setScheduledIds, scheduledIds]);

  // ✅ **Når schedule er ferdig, tilbakestill til standardvisning**
  useEffect(() => {
    if (!isOverriddenByScheduleRef.current && scheduledIds !== undefined) {
      const newContent = checkSchedule();
      if (newContent === null) {
        setScheduledIds(undefined);
      }
    }
    //eslint-disable-next-line
  }, [schedule, scheduledIds, setScheduledIds]);

  return { scheduledContentId: scheduledIds?.[0] };
}
